import { axios } from "@/client/libs/axios";
import type {
  EditableResumeField,
  OptimizationSuggestion,
} from "@/client/pages/dashboard/ai-optimization/review";

import {
  type ComponentTotals,
  type EditableComponent,
  isEditableComponent,
  type SuggestionGains,
} from "./coach-attributed-score";
import { isScoredFieldPath } from "./coach-scoreable-fields";
import type { SkillAddition } from "./coach-skill-additions";

/**
 * One AI call that SCORES the résumé against the JD and PLANS the improvements together,
 * attributing a score gain to each suggestion.
 *
 * WHY BOTH IN ONE CALL: the requirement is "accepting the AI-suggestions will increase the score
 * as per the accepted changes". That needs a per-suggestion gain, and only the thing that assessed
 * the résumé can say what fixing a given weakness is worth. Splitting it — score, then separately
 * ask for suggestions — gives two unrelated opinions and no way to attribute a click to a point.
 *
 * WHAT THIS FIXED: previously the panel re-scored the whole résumé after every accept and hoped
 * the number moved. It often did not, correctly, because the model was returning synonym swaps —
 * and with no attribution there was no way to tell a cosmetic suggestion from a substantive one.
 * A suggestion that cannot name a component and a gain is now dropped at the parser, which
 * eliminates rewords structurally rather than by heuristic: a reword has no gain to claim.
 *
 * ⚠️ ONLY ONE COMPONENT CAN BE TARGETED. `skillOverlap` is the only part of the match score a
 * résumé edit moves; `roleDomainFit` is judged from the candidate's stated dream roles and target
 * industries against the job's domain, never from the résumé, and CTC, location, experience band
 * and PRS come from the student's profile and the job. The prompt says so explicitly, because a
 * model told to "improve the match score" will otherwise write suggestions aimed at things a
 * résumé cannot change.
 *
 * ⚠️ NO FABRICATION. Gains must come from naming capabilities the résumé already evidences or
 * describing adjacent experience accurately. A prompt that bought score by inventing experience
 * would be putting false claims on a student's résumé.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

export type CoachPlanInput = {
  jobUuid?: string;
  jobDescription: string;
  resumeContent: string;
  editableFields: EditableResumeField[];
  /** Current sub-scores from the LMS, so the model calibrates gains against real numbers. */
  baseline: ComponentTotals;
  /**
   * How much each component is worth in the final score, from the LMS's own weighting.
   *
   * Given to the model because without it "gain" is a number in a vacuum. A point of
   * `skillOverlap` at weight 30 is worth 0.3 of a final score point; a point of
   * `roleDomainFit` at weight 20 is worth 0.2. Told which lever is heavier, the model spends
   * its effort where the score actually moves instead of spreading claims evenly.
   */
  weights: Record<string, number>;
  /** Skills the score is penalising, already narrowed to what this résumé lacks. */
  missingSkills: string[];
  /** Skills already credited, so a suggestion is not spent restating them. */
  presentSkills: string[];
};

/** A suggestion plus what the model says it is worth. */
export type PlannedSuggestion = OptimizationSuggestion & {
  targets: EditableComponent;
  /**
   * Sub-score points claimed by the model — FORCED TO 0 when the scorer cannot see this field.
   *
   * The model has no way to know which fields reach the scorer, so it is corrected here rather than
   * trusted. See coach-scoreable-fields.ts: `experience[].description` and every other narrative
   * field is invisible to `buildLlmStudentInputs`, so a rewrite there is worth exactly nothing to
   * the match score no matter how much better it reads.
   */
  gain: number;
  /** False when the scorer cannot see this field, so the card can say so instead of showing "+N". */
  scoreable: boolean;
  rationale: string;
  addsKeywords: string[];
};

export type CoachPlan = {
  /** The model's read of the résumé as it stands. Informational; the LMS baseline is authoritative. */
  assessed: ComponentTotals;
  /** Sub-scores the model believes are reachable if every suggestion is accepted. */
  projected: ComponentTotals;
  /** What the model read out of the JD. Logged, so a bad plan can be traced to a bad reading. */
  jobRequires: string[];
  suggestions: PlannedSuggestion[];
  /** Skills the résumé evidences but does not list. Still gated on evidence by the caller. */
  addSkills: SkillAddition[];
  gains: SuggestionGains;
};

export const buildScoreAndPlanPrompt = ({
  jobDescription,
  resumeContent,
  editableFields,
  baseline,
  weights,
  missingSkills,
  presentSkills,
}: CoachPlanInput) => {
  // The JD is the source of truth for what the job requires. The supplied list comes from
  // `Jobs.requiredSkills`, which is EMPTY on most jobs — every Product Manager row has `[]`. When
  // the prompt said "no specific skill gaps were detected" the model had nothing to aim at, so it
  // returned generic rewrites, which were then dropped as substanceless and the run failed with
  // "nothing safe to rewrite". Reading the JD removes that dependency entirely.
  const gapBlock =
    missingSkills.length > 0
      ? `\nSkills already identified as missing (a HINT, not the full list — read the job description for the rest):\n${missingSkills
          .map((skill) => `- ${skill}`)
          .join("\n")}\n`
      : `\nNo skill list was supplied for this job. Extract the required skills from the job
description text yourself, then compare them against the resume.\n`;

  // Not sliced. The LMS already bounds this list, and cutting it again here is the expensive
  // mistake in the pair: a present skill the model is not told about gets re-proposed, which
  // wastes part of the plan and claims a gain the scorer already credits.
  const coveredBlock =
    presentSkills.length > 0
      ? `\nAlready credited — do not spend a suggestion restating these:\n${presentSkills.join(
          ", ",
        )}\n`
      : "";

  /**
   * The scoring budget: where each component stands, how much room is left, and what a point
   * of it is worth in the final score.
   *
   * WHY SPELL THIS OUT. Without it the model was asked for "sub-score points" with no sense
   * of scale, so it produced claims that routinely exceeded the room available — six skills
   * at 8 points each into a 30-point gap — and spread them evenly across components of very
   * different weights. `distributeGains` scales overflow back down, but a plan that fits in
   * the first place distributes the student's attention better: the model can put its
   * strongest suggestions where the points actually are rather than having every claim
   * proportionally shrunk.
   *
   * Rounded to one decimal because these are guidance for a model, not arithmetic it needs to
   * reproduce exactly — the app recomputes every total itself.
   */
  const componentBudget = (name: "skillOverlap") => {
    const current = Math.round(baseline[name]);
    const weight = Number(weights?.[name]) || 0;
    const headroom = Math.max(0, 100 - current);
    // What filling the entire remaining gap would add to the final score.
    const worth = Math.round(((headroom * weight) / 100) * 10) / 10;

    return (
      `- ${name}: currently ${current}/100, weight ${weight} in the final score.\n` +
      `  Room left: ${headroom} points. Closing all of it would raise the final score by ` +
      `about ${worth}.\n` +
      `  Every 1 point of ${name} is worth ${Math.round((weight / 100) * 100) / 100} of a ` +
      `final score point.`
    );
  };

  const budgetBlock = `YOUR SCORING BUDGET — read this before assigning any "gain"

${componentBudget("skillOverlap")}

One rule follows from those numbers, and it decides whether your plan is usable:

THE GAINS YOU ASSIGN MUST SUM TO NO MORE THAN THE REMAINING ROOM. A component at 70 has 30
points left. Six suggestions claiming 8 each is 48 points into a 30-point gap — impossible,
and it makes every individual claim an overstatement. Decide the total skillOverlap can
realistically reach, then divide it between your suggestions.

Each "gain" is that suggestion's OWN share of skillOverlap's room — what accepting THAT ONE
change is worth, not what the whole plan is worth. Two suggestions of genuinely different
value must not carry the same number: the student sees these as "+N" on each card and decides
what to accept from them, so an evenly-spread plan tells them nothing about where to focus.`;

  return `You are a resume optimization assistant scoring a resume against a job description.

The match score has exactly ONE component a resume edit can change:

- "skillOverlap" (0-100): how completely the resume evidences the skills this job asks for.

A second component, "roleDomainFit", also contributes to the match score, but it is judged
entirely from the candidate's stated dream roles and target industries against the job's domain —
never from the résumé. A résumé edit CANNOT move it. Do not propose any change on the theory that
it will improve role/domain fit.

Everything else in the score (compensation expectations, location, years of experience, academic
record) also comes from the student's profile. NOTHING in the resume can change those. Do not
write suggestions aimed at them.

CRITICAL — WHAT THE SCORER READS

The scoring model is only given three things from the resume:

  1. the professional summary
  2. the skills list (skill NAMES only)
  3. experience entries as "Job Title @ Company"

It is NOT given experience descriptions, project descriptions, certifications or education text.
A rewrite of any of those cannot change the score by even one point, however much better it reads.

So the score moves in exactly two ways, and you should spend your effort accordingly:

  - ADD SKILLS to the skills list          → skillOverlap. The strongest lever by far.
  - RETITLE an existing skill to the job's own wording → skillOverlap.

You may still suggest rewrites of the summary and of experience and project narratives — they
matter to the recruiter who reads the resume, and a summary that reads as the right role still
helps the human reading it. Give them a small honest "gain"; the app will show them as improving
readability rather than score, and will not count them toward the projection. Do not inflate their
gain to make them look important.

${budgetBlock}
${gapBlock}${coveredBlock}
TASK

1. Read the job description and list the skills and the role/domain signals it actually asks for.
2. Compare them against the resume. Assess the resume's current roleDomainFit and skillOverlap.
3. Produce the set of changes that would raise them as far as HONESTLY possible, across the
   professional summary, experience and project narratives, and the skills section.
4. For each change, state which component it improves and how many sub-score points it adds.

TWO KINDS OF CHANGE

A. "suggestions" — rewrite the text of an editable field (summary, an experience narrative, a skill
   name). Use these to sharpen role/domain language and to describe work more precisely.

B. "addSkills" — add a skill to the skills section. THIS IS THE STRONGEST LEVER on skillOverlap.
   Propose every skill this job asks for that is not already listed, in two kinds:

   - confidence "evidenced": the resume's own text already demonstrates it. If the experience says
     "conducted regression and smoke testing before production releases" then Regression Testing is
     genuinely held and merely unlisted — listing it is accurate, and omitting it undercounts the
     candidate. Quote the supporting resume text in "evidence".
   - confidence "unverified": the job needs it, the resume does not show it, but it is plausibly
     adjacent to what this candidate has done. Propose it anyway and put in "evidence" the reason it
     is plausible — for example "QA engineers in Agile sprints usually do backlog grooming and
     requirement discovery; confirm if you have". The student decides whether it is true.

   Do NOT withhold a skill just because the resume does not prove it. The student knows their own
   history and will accept or reject each one. Withholding it denies them the chance to score higher
   on something they may genuinely have.

RULES

- Return JSON only.
- Suggest changes only for the editable fields listed below; keep each fieldPath exactly as given.
- previousText must be the field's current value. suggestedText is the replacement.
- NEVER invent employers, degrees, dates, metrics, tools or experience. For each gap either name a
  capability the resume already evidences, or describe adjacent experience accurately without
  overstating it, or leave the gap alone.
- NEVER add a skill name to a list, or to a sentence like "Skilled in X, Y, Z", as a way of
  claiming it. Inserting the words "Product Management" into a summary does not make the
  candidate a product manager — it puts a claim on their resume they would have to defend in an
  interview. A suggestion that closes a gap MUST describe the actual work that evidences it,
  naming what the candidate did. If there is no such work in the resume, the gap stays open.
- Your job is to get this candidate's score as HIGH as it can legitimately go. Be ambitious about
  what to propose and precise about what you claim. Always find something: reframe the summary
  toward the target role, surface skills the resume evidences but does not list, propose adjacent
  skills for the student to confirm, and sharpen experience narratives toward the job's language.
- Returning an empty plan is a failure. If the resume and the job are a poor match, that is exactly
  when the candidate needs the most help — propose the reframing and the additions that would close
  the distance, and let the student decide which are true.
- Every suggestion MUST carry a "targets" of "roleDomainFit" or "skillOverlap" and a "gain" > 0.
  If you cannot justify a gain, OMIT the suggestion — a reword with no substance is not an
  improvement and must not be returned.
- "gain" is sub-score points added to that component — this suggestion's own share, not the
  plan's total. Respect the budget above: the gains you assign to a component must sum to no
  more than the room it has left. A plan that overshoots is scaled back down proportionally,
  so overstating gains does not win the student any points — it only distorts which
  suggestions look important relative to each other.
- Differentiate the gains. A suggestion worth twice as much as another must say so. Identical
  numbers across every card tell the student nothing about where to spend their attention.
- BE COMPLETE. This is the student's ONE pass at this job. Cover every gap you can legitimately
  close, not a representative sample of them: every missing skill the job asks for, and every
  field whose wording can honestly be moved toward the role. A short plan is only correct when
  the resume genuinely has few remaining gaps — never as a way of keeping the output tidy.
- Completeness is about COVERAGE, never about lowering the bar. Do not pad the plan with
  rewords, near-duplicate skills, or suggestions you cannot attach a real gain to. Ten
  substantive changes are better than five; five substantive changes are better than ten
  padded with cosmetic ones. Every rule above still applies to every entry you add.

JSON shape:
{
  "assessed":  { "roleDomainFit": 0, "skillOverlap": 0 },
  "projected": { "roleDomainFit": 0, "skillOverlap": 0 },
  "jobRequires": ["skills and signals you read out of the job description"],
  "suggestions": [
    {
      "sectionId": "string",
      "sectionName": "string",
      "itemLabel": "string",
      "fieldPath": "string",
      "previousText": "string",
      "suggestedText": "string",
      "targets": "roleDomainFit" | "skillOverlap",
      "gain": 0,
      "rationale": "one short sentence on why this earns those points",
      "addsKeywords": ["string"]
    }
  ],
  "addSkills": [
    {
      "name": "skill to add, as the job description words it",
      "description": "short grouping label, matching how the resume labels its other skills",
      "confidence": "evidenced" | "unverified",
      "evidence": "the resume text that demonstrates it, or why it is plausible for this candidate",
      "gain": 0
    }
  ]
}

Target Job Description:
"""
${jobDescription.trim()}
"""

Candidate Resume:
"""
${resumeContent.trim()}
"""

Editable Fields:
${JSON.stringify(editableFields ?? [], null, 2)}

Return JSON only:`;
};

const parseJsonPayload = (value: string): Record<string, unknown> => {
  const trimmed = String(value ?? "").trim();
  // Models wrap JSON in a fenced block often enough that not handling it would be a routine
  // failure rather than an edge case.
  const fenced = /```(?:json)?\s*([\s\S]*?)\s*```/i.exec(trimmed);

  return JSON.parse(fenced?.[1] ?? trimmed) as Record<string, unknown>;
};

const readTotals = (value: unknown): ComponentTotals => {
  const source = (value ?? {}) as Record<string, unknown>;
  const read = (key: string) => {
    const number = Number(source[key]);
    return Number.isFinite(number) ? Math.min(100, Math.max(0, number)) : 0;
  };

  return { roleDomainFit: read("roleDomainFit"), skillOverlap: read("skillOverlap") };
};

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => String(entry ?? "").trim()).filter(Boolean) : [];

const toSuggestionId = (fieldPath: string) =>
  fieldPath.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Parse the plan, dropping anything that cannot state what it is worth.
 *
 * Deliberately strict. A suggestion with no `targets`/`gain` is exactly the cosmetic rewrite this
 * redesign exists to eliminate, and letting one through would put a card in front of the student
 * that moves the score by zero when accepted.
 */
export const parseCoachPlan = (raw: string): CoachPlan => {
  const payload = parseJsonPayload(raw);
  const entries = Array.isArray(payload.suggestions) ? payload.suggestions : [];

  const suggestions: PlannedSuggestion[] = [];
  const gains: SuggestionGains = {};

  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    const source = entry as Record<string, unknown>;

    const fieldPath = String(source.fieldPath ?? "").trim();
    const previousText = String(source.previousText ?? "").trim();
    const suggestedText = String(source.suggestedText ?? "").trim();
    const targets = source.targets;
    const gain = Number(source.gain);

    if (!fieldPath || !previousText || !suggestedText) continue;
    if (previousText === suggestedText) continue;
    if (!isEditableComponent(targets)) continue;
    if (!Number.isFinite(gain) || gain <= 0) continue;

    const id = toSuggestionId(fieldPath);

    // THE CORRECTION THAT MAKES THE PROJECTION HONEST. The model claims a gain for every suggestion
    // — it has no way to know that the LMS scorer only receives the summary, the skill names and
    // experience titles/companies. A rewrite of anything else moves the real score by zero, so its
    // claim is discarded here rather than carried into the projection and promised to the student.
    //
    // The suggestion itself survives: rewriting an experience bullet toward the JD is worth doing
    // for the human reading the résumé. It just is not worth points.
    const scoreable = isScoredFieldPath(fieldPath);
    const effectiveGain = scoreable ? Math.min(100, gain) : 0;

    suggestions.push({
      id,
      sectionId: String(source.sectionId ?? "resume"),
      sectionName: String(source.sectionName ?? "Resume"),
      itemLabel: String(source.itemLabel ?? "Content"),
      fieldPath,
      previousText,
      suggestedText,
      targets,
      gain: effectiveGain,
      scoreable,
      rationale: String(source.rationale ?? "").trim(),
      addsKeywords: asStringArray(source.addsKeywords),
    });

    // Only scoreable suggestions enter `gains`. `buildScoreProjection` sums this map, so an
    // unscoreable entry here — even at gain 0 — would still be counted in `acceptedCount` and make
    // the "+N from M accepted change(s)" line claim credit for changes that earned none.
    if (scoreable) gains[id] = { targets, gain: effectiveGain };
  }

  // Skill additions always target skillOverlap — that is what listing a skill moves. Evidence is
  // required here and verified against the résumé text by the caller; a skill with none is the
  // keyword stuffing this feature has already shipped once.
  const addSkills: SkillAddition[] = [];
  const additionEntries = Array.isArray(payload.addSkills) ? payload.addSkills : [];

  for (const entry of additionEntries) {
    if (!entry || typeof entry !== "object") continue;
    const source = entry as Record<string, unknown>;

    const name = String(source.name ?? "").trim();
    const evidence = String(source.evidence ?? "").trim();
    const gain = Number(source.gain);

    if (!name || !evidence) continue;
    if (!Number.isFinite(gain) || gain <= 0) continue;

    const id = `add-skill-${toSuggestionId(name.toLowerCase())}`;

    addSkills.push({
      id,
      name,
      description: String(source.description ?? "").trim(),
      evidence,
      // Provisional. classifySkillAdditions recomputes this against the résumé, because the model
      // has an incentive to call everything evidenced and this label decides whether the student is
      // asked to confirm.
      confidence: source.confidence === "evidenced" ? "evidenced" : "unverified",
      targets: "skillOverlap",
      gain: Math.min(100, gain),
    });

    gains[id] = { targets: "skillOverlap", gain: Math.min(100, gain) };
  }

  return {
    assessed: readTotals(payload.assessed),
    projected: readTotals(payload.projected),
    jobRequires: asStringArray(payload.jobRequires),
    suggestions,
    addSkills,
    gains,
  };
};

/**
 * Ask for the plan. Uses the same `/auth/resumeOptimizationPrompt` route the dashboard flow uses —
 * it takes a prompt and returns the model's reply, so only the prompt differs and the server is
 * untouched.
 */
export const requestCoachPlan = async (input: CoachPlanInput): Promise<CoachPlan> => {
  const response = await axios.post<{ message?: string | number }>(
    input.jobUuid ? "/coach/optimization-plan" : "/auth/resumeOptimizationPrompt",
    {
      prompt: buildScoreAndPlanPrompt(input),
      ...(input.jobUuid ? { jobUuid: input.jobUuid } : {}),
    },
  );

  const message = response.data?.message;

  return parseCoachPlan(typeof message === "string" ? message : String(message ?? ""));
};
